import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { ArrowLeft, FileText, Book, Lightbulb, Info, ChevronLeft, ChevronRight, Play, Maximize, Minimize, X } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

// Simple KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef} 
      className={`relative overflow-hidden rounded-lg border ${isFullscreen ? 'bg-white flex flex-col h-screen' : 'bg-gray-50'}`}
    >
      {/* Slide content */}
      <div className="relative flex-grow">
        <div className={`p-6 ${isFullscreen ? 'h-[calc(100vh-70px)] overflow-y-auto' : 'max-h-[70vh] overflow-y-auto'} thin-scrollbar`}>
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-xl font-medium text-gray-900">
              {slides[currentSlide].title}
            </h3>
            <div className="flex space-x-2">
              <button 
                onClick={toggleFullscreen}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
              
              {isFullscreen && (
                <button 
                  onClick={() => document.exitFullscreen()}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
          
          <div className="prose max-w-none">
            {slides[currentSlide].content}
          </div>
        </div>
      </div>

      {/* Navigation controls - now always visible in fullscreen too */}
      <div className={`flex items-center justify-between p-4 border-t bg-white ${isFullscreen ? 'mt-auto' : ''}`}>
        <div className="text-sm text-gray-500">
          Slide {currentSlide + 1} of {slides.length}
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={nextSlide}
            aria-label="Next slide"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Video placeholder component
const VideoPlaceholder = () => {
  return (
    <div className="bg-gray-100 rounded-xl overflow-hidden my-10">
      <div className="aspect-video bg-gray-200 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/20"></div>
        <div className="text-center">
          <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <Play className="h-8 w-8" />
          </div>
          <p className="text-gray-700 font-medium">Watch Full Lecture Video</p>
          <p className="text-sm text-gray-500 mt-1">45 minutes · Premium Content</p>
        </div>
      </div>
    </div>
  );
};

export default function WhatIsLongevityMedicine() {
  const [_, navigate] = useLocation();
  
  // Lecture slides data
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Introduction: Context, Definitions, and Why It Matters",
      content: (
        <>
          <p>For as long as humans have cared for one another, we've sought ways to ease suffering and prolong life. From the earliest healers chanting prayers by firelight to modern laboratories running genomic analyses, medicine has tracked along a winding path of discovery and reinvention.</p>
          
          <p>Today, we find ourselves standing at a fascinating crossroads. The discoveries of the past two centuries have collectively transformed human health, yet we face new questions:</p>
          
          <ul className="list-disc list-inside space-y-2 my-4 ml-4">
            <li>What if we could detect the earliest signs of deterioration, years before symptoms emerge?</li>
            <li>What if we could tailor interventions to an individual's unique biochemistry?</li>
            <li>What if we could slow—or even partially reverse—the biological processes of aging?</li>
          </ul>
          
          <p>These questions lie at the heart of longevity medicine. Longevity medicine isn't just about adding more years to life; it's about improving the quality of each additional year. It emphasizes the idea that what truly matters isn't just living longer, but living better—extending one's "healthspan."</p>
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h3 className="text-lg font-medium mb-2">What is Healthspan?</h3>
            <p>Healthspan refers to the proportion of one's lifespan spent in good health, free from debilitating conditions and functional limitations. In a way, longevity medicine strives to compress morbidity—pushing disease and disability toward the very end of a lengthened life.</p>
          </div>
          
          <p>This lesson provides a comprehensive overview of how we arrived at this point in medical history and why longevity medicine represents the logical next step in healthcare's evolution.</p>
        </>
      )
    },
    {
      id: 2,
      title: "Spiritual Roots and Early Healers",
      content: (
        <>
          <p>Before laboratory coats, stethoscopes, and randomized controlled trials, the experience of healing was deeply intertwined with spirituality and communal beliefs. Millennia ago, in cultures around the world, healers were often also spiritual leaders, shamans, or priests.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Ancient Medical Texts</h3>
              <p>The Ebers Papyrus from ancient Egypt (around 1550 BCE) contained a litany of remedies and incantations for treating common diseases.</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-amber-800 mb-2">Traditional Healing Ceremonies</h3>
              <p>Spiritual ceremonies, chants, and prayers were standard "treatments," aiming to harmonize the sick individual with both unseen forces and the visible community.</p>
            </div>
          </div>
          
          <p>Interestingly, though these methods may lack the empirical rigor we expect today, it would be erroneous to dismiss them outright. Many herbal remedies had real, if not fully understood, medicinal properties, and the act of ritual healing offered psychological, social, and emotional support—factors that can have a tangible impact on health outcomes.</p>
          
          <KeyTakeaway>
            For longevity medicine, these historical roots serve as a reminder to maintain perspective. Even as we explore genetic testing, telomere length, and advanced biomarkers, we can remain open to the idea that human health is broader than any one dimension.
          </KeyTakeaway>
          
          <p>Modern technology offers data; traditional forms of medicine offer an understanding of a patient's psychosocial and spiritual well-being. The most compelling future of healthcare may ultimately integrate both perspectives.</p>
        </>
      )
    },
    {
      id: 3,
      title: "Medicine Before Germ Theory",
      content: (
        <>
          <p>Moving closer to the era that directly precedes our modern understanding, we arrive at a time when miasma theory reigned supreme. In the centuries prior to the mid-1800s, the prevailing notion in many parts of the world was that diseases were transmitted by "bad air" or noxious vapors.</p>
          
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 my-4 rounded-r-md">
            <h3 className="text-lg font-medium text-indigo-800 mb-2">Miasma Theory</h3>
            <p>The belief that foul-smelling air from rotting organic matter, sewage, or stagnant water caused diseases led to some effective public health measures (improved sanitation) despite incorrect reasoning.</p>
          </div>
          
          <p>During this period, medical practices were still a mosaic of tradition, anecdotal success, and fervent guesswork:</p>
          
          <ul className="list-disc list-inside space-y-2 my-4 ml-4">
            <li><strong>Bloodletting</strong>: Widely practiced as a means of "restoring balance" within the body.</li>
            <li><strong>Humoral theory</strong>: Diseases were believed to stem from imbalances in the four "humors" (blood, phlegm, black bile, and yellow bile).</li>
            <li><strong>Herbal remedies</strong>: Physicians had some successes with herbal tinctures, purgatives, and topical ointments.</li>
          </ul>
          
          <p>Nevertheless, we can see the early glimmers of an analytical mindset in some notable physicians:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-gray-100 p-4 rounded-md">
              <h4 className="font-medium mb-1">Andreas Vesalius (16th century)</h4>
              <p className="text-sm">Pioneered a more anatomical understanding of the human body.</p>
            </div>
            <div className="bg-gray-100 p-4 rounded-md">
              <h4 className="font-medium mb-1">William Harvey (17th century)</h4>
              <p className="text-sm">Described the circulatory system, moving medicine toward physiological understanding.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Medicine before germ theory also serves as a historical reminder of the limits of observational bias. Observing a correlation—bad smells and disease, for instance—doesn't necessarily establish causation. The shift from correlation to causation is one of the hallmarks of modern scientific inquiry.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "The Emergence of Germ Theory",
      content: (
        <>
          <p>Few events in the history of medicine rival the impact of germ theory. Between the mid-19th and early 20th centuries, pioneering scientists like Louis Pasteur and Robert Koch demonstrated that microorganisms (bacteria, viruses) were responsible for specific diseases.</p>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-medium text-blue-900 mb-4">Key Contributions to Germ Theory</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Louis Pasteur</h4>
                  <p className="text-gray-700">Debunked spontaneous generation and demonstrated how microorganisms could be controlled through pasteurization.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Robert Koch</h4>
                  <p className="text-gray-700">Formulated Koch's postulates, a series of criteria used to link specific microbes to specific diseases.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Public Health Revolution</h4>
                  <p className="text-gray-700">Clean water supplies, sewage systems, and improved sanitation led to dramatic reductions in diseases like cholera and typhoid.</p>
                </div>
              </div>
            </div>
          </div>
          
          <p>Germ theory also heralded the rise of vaccinations. Although Edward Jenner had famously introduced a smallpox vaccine in the late 18th century, Pasteur's experiments with vaccines for anthrax and rabies clarified the underlying principle: the immune system could be "trained" to resist infectious agents.</p>
          
          <KeyTakeaway>
            What does germ theory have to do with longevity medicine? In short, it created a foundation of knowledge that drastically extended the average human lifespan. By dramatically reducing mortality from infectious diseases, the focus in developed nations shifted to chronic, degenerative conditions—heart disease, cancer, diabetes—which now cause the majority of deaths.
          </KeyTakeaway>
          
          <p>The success against infectious disease thus set the stage for the new frontier: how to effectively combat the diseases of aging and lifestyle.</p>
        </>
      )
    },
    {
      id: 5,
      title: "Biostatistics and Data-Driven Insights",
      content: (
        <>
          <p>The acknowledgment that microorganisms cause disease introduced a new era of public health intervention. However, the ability to systematically measure, track, and analyze health data on a population level required another important development: biostatistics.</p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 my-6">
            <h3 className="text-lg font-medium text-blue-800 mb-3">John Snow and the Broad Street Pump</h3>
            <p>John Snow is famous for his investigation of the cholera outbreak in London's Broad Street area in 1854. By plotting cholera cases on a map, he identified a water pump as the epicenter of the outbreak. Removing the handle on that pump is said to have dramatically reduced new cases.</p>
            <p className="mt-2">More importantly, Snow's approach illustrated how systematic data collection and spatial analysis could uncover the source of an epidemic—demonstrating the power of data to shape health policy and clinical practice.</p>
          </div>
          
          <p>Following Snow's example, subsequent researchers began using statistical models to interpret health data. They adopted:</p>
          
          <ul className="list-disc list-inside space-y-2 my-4 ml-4">
            <li>Cohort studies</li>
            <li>Case-control studies</li>
            <li>Randomized controlled trials (RCTs)</li>
          </ul>
          
          <p>The field of biostatistics provided essential tools to distinguish between correlation and causation, factoring in confounding variables, sample size, and variance. As a result, clinical medicine increasingly aligned with an evidence-based approach, guided by systematically gathered data rather than anecdotes or tradition.</p>
          
          <KeyTakeaway>
            In longevity medicine, biostatistics plays a pivotal role. Researchers are investigating longitudinal cohorts—tracking individuals over years or even decades—to understand which markers best predict healthy aging. By examining correlations between specific biomarkers and clinical outcomes, they hope to pinpoint the interventions that most powerfully prevent frailty and chronic disease.
          </KeyTakeaway>
          
          <p>However, biostatistics also reminds us to remain cautious. In a world inundated with health claims, every conclusion must be assessed for methodological rigor. Longevity science is no different. The field is rife with new findings, some of which hold up under scrutiny and others that do not.</p>
        </>
      )
    },
    {
      id: 6,
      title: "The Era of Evidence-Based Medicine (EBM)",
      content: (
        <>
          <p>As statistical approaches grew more sophisticated, they merged with the formalization of the scientific method in clinical practice to create what is now known as Evidence-Based Medicine (EBM). EBM is often credited to pioneers like David Sackett, who in the 1990s emphasized the integration of the best research evidence with clinical expertise and patient values.</p>
          
          <div className="my-6">
            <h3 className="text-lg font-medium mb-3">The Hierarchy of Evidence</h3>
            <div className="relative">
              <div className="bg-gradient-to-r from-emerald-100 to-green-100 p-5 rounded-t-lg">
                <h4 className="font-medium">Systematic Reviews & Meta-Analyses</h4>
                <p className="text-sm">Pool data from multiple RCTs to derive a more conclusive picture</p>
              </div>
              <div className="bg-gradient-to-r from-blue-100 to-indigo-100 p-5">
                <h4 className="font-medium">Randomized Controlled Trials</h4>
                <p className="text-sm">Participants randomly assigned to interventions or control</p>
              </div>
              <div className="bg-gradient-to-r from-purple-100 to-blue-100 p-5">
                <h4 className="font-medium">Observational Studies</h4>
                <p className="text-sm">Cohort or case-control analyses</p>
              </div>
              <div className="bg-gradient-to-r from-amber-100 to-yellow-100 p-5 rounded-b-lg">
                <h4 className="font-medium">Case Reports & Expert Opinion</h4>
                <p className="text-sm">Helpful but subject to bias or limited scope</p>
              </div>
            </div>
          </div>
          
          <p>This framework has dramatically improved the consistency and reliability of clinical care. Instead of relying on tradition or the authority of senior physicians, EBM encourages healthcare providers to consult up-to-date research, systematically reviewed guidelines, and validated protocols.</p>
          
          <KeyTakeaway>
            The success of EBM has laid the groundwork for the next frontier: personalized and precision medicine. As we gather more data and develop better biomarker tests, it becomes plausible to parse out those subgroups or to design sophisticated trials that can address individual variability. Rather than discarding EBM, longevity medicine aims to refine and augment it.
          </KeyTakeaway>
          
          <p>Moreover, the principles behind EBM—critical thinking, systematic assessment of evidence, transparency in methodology—remain indispensable. They offer the best protection against snake oil, hype, and inflated claims.</p>
        </>
      )
    },
    {
      id: 7,
      title: "Personalized and Preventive Care (Where We Are Now)",
      content: (
        <>
          <p>Building on the foundations of EBM, modern healthcare has begun to recognize that no single treatment plan is universally ideal. Over the past two decades, breakthroughs in genomics, biomarker research, and digital health tracking have illuminated how varied individual biology can be.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Genomics</h3>
              <p>Since the Human Genome Project, researchers have identified SNPs that influence everything from drug metabolism to disease susceptibility.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-green-800 mb-2">Biomarkers</h3>
              <p>Blood-based indicators of inflammation, hormone levels, and advanced imaging findings help physicians detect early shifts in physiology before symptoms appear.</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">Digital Health</h3>
              <p>Wearables, continuous glucose monitors, and other devices generate constant streams of physiological data that can be analyzed for personalized insights.</p>
            </div>
          </div>
          
          <p>Beyond technology, there's a cultural shift in the role of the physician. No longer are physicians simply gatekeepers to prescriptions or specialists in acute interventions. Increasingly, they're viewed as partners, collaborators, or health coaches, working with informed patients who want guidance on everything from optimizing energy levels to balancing circadian rhythms for better sleep.</p>
          
          <KeyTakeaway>
            In longevity medicine, the central query isn't: "What drug do I prescribe to treat Condition X?" Instead, it's: "What combination of interventions will allow this individual to maintain and even enhance their physical and cognitive function over the next 10, 20, or 30 years?"
          </KeyTakeaway>
          
          <p>This question naturally spans diet, exercise, stress management, hormone balance, genetic predispositions, and psychological well-being. At the same time, a purely data-driven approach can miss intangible but critical dimensions of health. Emotions, mental outlook, relationships, and sense of purpose all shape an individual's capacity to adhere to interventions and to flourish over a long lifespan.</p>
        </>
      )
    },
    {
      id: 8,
      title: "Reconnecting with a Broader Vision of Health",
      content: (
        <>
          <p>It might seem paradoxical that, after centuries spent shedding spiritual and mystical frameworks in favor of scientific rigor, modern medicine would circle back to embrace ideas that have a "holistic" or even "spiritual" dimension. And yet, that is precisely what is happening in many circles.</p>
          
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-6 rounded-r-md">
            <h3 className="text-lg font-medium text-amber-800 mb-2">Mind-Body Connections and Health</h3>
            <p>Stress is a well-documented accelerator of aging via mechanisms such as chronic inflammation, dysregulated cortisol rhythms, and oxidative stress. Methods once marginalized—like mindfulness and meditation—are supported by a growing body of studies demonstrating their ability to lower blood pressure, reduce anxiety, and possibly slow cellular aging markers.</p>
          </div>
          
          <p>In parallel, we're witnessing a renewed dialogue about the meaning of health. Is health merely the absence of disease, or does it represent a positive state of thriving—physically, mentally, and emotionally?</p>
          
          <ul className="list-disc list-inside space-y-2 my-4 ml-4">
            <li>What motivates us to pursue longevity in the first place?</li>
            <li>Is it the desire to experience life's richness for as long as possible?</li>
            <li>To remain active participants in our communities?</li>
            <li>For personal growth, creativity, and the joy of connecting with others?</li>
          </ul>
          
          <KeyTakeaway>
            This confluence of the spiritual and the scientific is not about discarding the evidence-based paradigm. Rather, it's about layering additional dimensions into the personalized approach. A patient's sense of purpose, worldview, and emotional well-being can influence how they respond to a particular intervention.
          </KeyTakeaway>
          
          <p>In longevity medicine, these factors can determine whether a meticulously crafted plan is actually followed. Interventions are only as good as the patient's consistency and adherence. The recognition that deeper values and beliefs drive behavior is why many longevity clinics and programs incorporate coaching, community support, and even psychological or spiritual counseling.</p>
          
          <p>A broader vision of health also reintroduces the idea of "integrative" approaches. This entails weaving together validated interventions—nutritional science, exercise physiology, stress management, emotional resilience training—into an overarching, individualized tapestry of well-being.</p>
        </>
      )
    },
    {
      id: 9,
      title: "Why Longevity Medicine? The Logical Next Step",
      content: (
        <>
          <p>Given how far medicine has come—taming most infectious scourges, forging robust data analysis techniques, and establishing an evidence-based framework—why do we need another paradigm? What gap does longevity medicine seek to fill?</p>
          
          <div className="space-y-6 my-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Early Intervention</h3>
              <p>Traditional medical practice often addresses chronic conditions once they're advanced. In contrast, longevity medicine looks for early warning signals, leveraging tools such as continuous biomonitoring or advanced labs to intervene while there's still ample time to alter the course.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-green-800 mb-2">Extending Healthspan</h3>
              <p>Longevity medicine strives to extend not just lifespan but healthspan. The aim is to compress morbidity—pushing serious illness and disability toward the very end of life. This shifts the focus to functional fitness, cognitive resilience, and metabolic health maintained deep into later decades.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-purple-800 mb-2">Personalization</h3>
              <p>People vary in their genetic makeup, microbiome composition, socio-economic circumstances, personal preferences, and psychological inclinations. Longevity medicine invests heavily in refining interventions based on unique biomarker profiles and personal goals.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Longevity medicine is more than a medical specialty; it's a philosophy of living that prioritizes continuous learning, adaptation, and self-experimentation within a scientifically sound framework. That sense of empowerment—paired with an eye toward long-term, proactive health—may prove critical as we face new challenges like aging populations, rising healthcare costs, and the complexities of chronic disease management.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 10,
      title: "Conclusion and Looking Ahead",
      content: (
        <>
          <p>We've traced the arc of medicine from its deep spiritual roots to the modern era of high-tech, data-driven care. We've seen how germ theory revolutionized our battle against infectious disease, freeing us up to focus on chronic and degenerative illnesses. We've examined how biostatistics paved the way for systematic inquiry, spawning the era of Evidence-Based Medicine, which has saved countless lives through protocols and guidelines grounded in rigorous data.</p>
          
          <p>Now, as we stand in the era of rapid technological progress, medicine appears to be converging once again with a holistic mindset—melding precise interventions with a renewed appreciation for each individual's values, sense of purpose, and personal context.</p>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-serif font-medium text-blue-900 mb-4">The Future of Longevity Medicine</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">AI-Based Analytics</h4>
                  <p className="text-gray-700">Integration of genomic data, wearables, lab tests, and imaging to provide an ever clearer picture of how each person is aging.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Advanced Biomarkers</h4>
                  <p className="text-gray-700">Discovery of new biomarkers that offer real-time snapshots of cellular aging processes.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Regenerative Therapies</h4>
                  <p className="text-gray-700">Senolytic drugs—designed to clear "zombie cells" that accumulate with age—are already in early human trials.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Personalized Nutrition</h4>
                  <p className="text-gray-700">The frontier for personalized dietary interventions may expand, driven by microbiome research that pinpoints how individual gut flora respond to different nutrients.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            The future of longevity medicine is about balancing the demands of robust scientific validation with the desire to innovate and explore new interventions. It draws on the legacy of our predecessors—spiritual healers who recognized the importance of caring for the whole person, scientists who demanded rigorous proof, visionaries who introduced new ways of thinking about disease prevention, and data pioneers who showed us how to glean insights from numbers.
          </KeyTakeaway>
          
          <div className="mt-6 text-sm text-gray-600 border-t pt-4">
            <p className="font-medium">References and Suggested Readings</p>
            <ul className="space-y-1 mt-2">
              <li>Mukherjee, Siddhartha. The Emperor of All Maladies: A Biography of Cancer.</li>
              <li>Harari, Yuval Noah. Sapiens: A Brief History of Humankind.</li>
              <li>Johnson, Steven. The Ghost Map: The Story of London's Most Terrifying Epidemic.</li>
              <li>Greenhalgh, Trisha. How to Read a Paper: The Basics of Evidence-Based Medicine.</li>
              <li>Attia, Peter. Outlive: The Science and Art of Longevity.</li>
              <li>Sinclair, David, with LaPlante, Matthew. Lifespan: Why We Age—and Why We Don't Have To.</li>
            </ul>
          </div>
        </>
      )
    }
  ];


  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow max-w-5xl">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/lectures">Lectures</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>What Is Longevity Medicine</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-[#3498DB]"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
          
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Lecture Material
          </div>
        </div>
        
        <div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
            What Is Longevity Medicine?
          </h1>
          <p className="text-[#7F8C8D] mb-6">
            A comprehensive exploration of medicine's evolution from spiritual practices to 
            evidence-based medicine and the emergence of longevity-focused healthcare.
          </p>
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <FileText className="h-4 w-4 mr-1" />
              <span>Lecture Duration: 45 minutes</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-300"></div>
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <Book className="h-4 w-4 mr-1" />
              <span>Last Updated: April 15, 2025</span>
            </div>
          </div>
          
          <Separator />
          
          <Slideshow slides={lectureSlides} />
          
          {/* Accompanying Audio */}
          <div className="mt-8 flex justify-center">
            <iframe 
              src="https://share.descript.com/embed/6rAPr0XyMq3" 
              width="640" 
              height="360" 
              frameBorder="0" 
              allowFullScreen
              className="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}